// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react'
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material'
import { loadFkspRequests, updateFkspRequestStatus, deleteFkspRequest, downloadFkspPdf } from '../lib/apiPhpAdapter'
import { FkspRequest } from '../types'
import { format } from 'date-fns'
import { FkspRequestModal } from '../components/FkspRequestModal'
import { FkspRequestsToolbar } from '../components/FkspRequestsToolbar'
import { FkspTablePagination, fkspPageSlice } from '../components/FkspTablePagination'
import { filterAndSortFkspRequests } from '../lib/fkspListFilters'
import type { FkspEkonomSortField } from '../lib/fkspConstants'
import { FKSP_LIST_FETCH_LIMIT } from '../lib/fkspConstants'
import { fkspStatusChipColor, fkspStatusLabelCs } from '../lib/fkspStatusLabels'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'

export const FkspZocmosView: React.FC = () => {
  const { user } = useAuth()
  const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'
  const [requests, setRequests] = useState<FkspRequest[]>([])
  const [listLoaded, setListLoaded] = useState(false)
  const [toast, setToast] = useState<{show: boolean, msg: string, type: 'success'|'error'}>({show: false, msg: '', type: 'success'})

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<FkspRequest | null>(null)
  const [actionType, setActionType] = useState<'approve'|'reject'|null>(null)
  const [note, setNote] = useState('')
  const [selectedReq, setSelectedReq] = useState<FkspRequest | null>(null)
  const [actionLoading, setActionLoading] = useState<boolean>(false)
  const [listSearch, setListSearch] = useState('')
  const [listCreatedFrom, setListCreatedFrom] = useState('')
  const [listCreatedTo, setListCreatedTo] = useState('')
  const [listSortField, setListSortField] = useState<FkspEkonomSortField>('created_at')
  const [listDateNewestFirst, setListDateNewestFirst] = useState(true)
  const [pendingPage, setPendingPage] = useState(0)
  const [historyPage, setHistoryPage] = useState(0)

  const fkspFilterParams = useMemo(
    () => ({
      searchQuery: listSearch,
      dateNewestFirst: listDateNewestFirst,
      createdFrom: listCreatedFrom,
      createdTo: listCreatedTo,
      sortField: listSortField,
    }),
    [listSearch, listDateNewestFirst, listCreatedFrom, listCreatedTo, listSortField],
  )

  useEffect(() => {
    fetchRequests()
  }, [])

  const fetchRequests = async () => {
    try {
      const data = await loadFkspRequests('zocmos', { limit: FKSP_LIST_FETCH_LIMIT })
      setRequests(data)
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    } finally {
      setListLoaded(true)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: listLoaded,
    onFound: (r) => setSelectedReq(r),
    onNotFound: () =>
      setToast({ show: true, msg: 'Žádost nenalezena nebo již byla zpracována.', type: 'error' }),
  })

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return
    try {
      await updateFkspRequestStatus(selectedRequest.id, 'zocmos', actionType, note)
      setToast({ show: true, msg: 'Stav byl úspěšně změněn', type: 'success' })
      setDialogOpen(false)
      fetchRequests()
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    }
  }

  const openActionDialog = (req: FkspRequest, action: 'approve'|'reject') => {
    setSelectedRequest(req)
    setActionType(action)
    setNote('')
    setDialogOpen(true)
  }

  const pendingRequests = requests.filter(r => r.status === 'zocmos_pending')
  const historyRequests = requests.filter(r => r.status !== 'zocmos_pending' && r.status !== 'new' && r.status !== 'personalista_pending')

  const displayPending = useMemo(
    () => filterAndSortFkspRequests(pendingRequests, fkspFilterParams),
    [pendingRequests, fkspFilterParams],
  )
  const displayHistory = useMemo(
    () => filterAndSortFkspRequests(historyRequests, fkspFilterParams),
    [historyRequests, fkspFilterParams],
  )

  useEffect(() => {
    setPendingPage(0)
    setHistoryPage(0)
  }, [listSearch, listCreatedFrom, listCreatedTo, listSortField, listDateNewestFirst])

  const pagedPending = useMemo(
    () => fkspPageSlice(displayPending, pendingPage),
    [displayPending, pendingPage],
  )
  const pagedHistory = useMemo(
    () => fkspPageSlice(displayHistory, historyPage),
    [displayHistory, historyPage],
  )

  const rowSxIfProcessed = (req: FkspRequest) =>
    req.financist_processed_at ? { bgcolor: '#fff9c4' } : {}

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        FKSP - Panel ZO ČMOS
      </Typography>
      <FkspRequestsToolbar
        search={listSearch}
        onSearchChange={setListSearch}
        createdFrom={listCreatedFrom}
        onCreatedFromChange={setListCreatedFrom}
        createdTo={listCreatedTo}
        onCreatedToChange={setListCreatedTo}
        sortField={listSortField}
        onSortFieldChange={setListSortField}
        dateNewestFirst={listDateNewestFirst}
        onDateSortChange={setListDateNewestFirst}
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" mb={3} color="primary">Žádosti čekající na vyjádření odborů ({displayPending.length})</Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Datum</TableCell>
                  <TableCell>Žadatel</TableCell>
                  <TableCell>Typ / Účel</TableCell>
                  <TableCell align="right">Částka</TableCell>
                  <TableCell align="center">Akce</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>Žádné nové žádosti</TableCell></TableRow>
                ) : displayPending.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>Žádná žádost neodpovídá vyhledávání</TableCell></TableRow>
                ) : (
                  pagedPending.map(req => (
                    <TableRow 
                      key={req.id}
                      hover
                      onClick={() => setSelectedReq(req)}
                      sx={{ cursor: 'pointer', ...rowSxIfProcessed(req) }}
                    >
                      <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{req.requester_name}</Typography>
                        <Typography variant="caption" color="text.secondary">Os. č: {req.personal_number}</Typography>
                      </TableCell>
                      <TableCell>
                        {req.type === 'benefity' ? 'Benefity' : 'Připojištění'}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {req.type === 'benefity' ? req.benefit_type : req.institution_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {req.type === 'benefity' ? req.amount : req.fksp_contribution} Kč
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="contained" color="success" sx={{ mr: 1 }} onClick={(e) => { e.stopPropagation(); openActionDialog(req, 'approve'); }}>Doporučit</Button>
                        <Button size="small" variant="outlined" color="error" onClick={(e) => { e.stopPropagation(); openActionDialog(req, 'reject'); }}>Nedoporučit</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FkspTablePagination
            count={displayPending.length}
            page={pendingPage}
            onPageChange={setPendingPage}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={3}>Historie</Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Datum</TableCell>
                  <TableCell>Žadatel</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Rozhodnutí ZO</TableCell>
                  <TableCell>Akt. Stav</TableCell>
                  <TableCell align="center">Ekonom</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      {historyRequests.length === 0 ? 'Žádná historie' : 'Žádná žádost neodpovídá vyhledávání'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedHistory.map(req => (
                  <TableRow
                    key={req.id}
                    hover
                    onClick={() => setSelectedReq(req)}
                    sx={{ cursor: 'pointer', ...rowSxIfProcessed(req) }}
                  >
                    <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                    <TableCell>{req.requester_name}</TableCell>
                    <TableCell>{req.type === 'benefity' ? 'Benefity' : 'Připojištění'}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {req.zocmos_status === 'approved' ? <Chip label="Doporučeno" color="success" size="small"/> : 
                       req.zocmos_status === 'rejected' ? <Chip label="Nedoporučeno" color="error" size="small"/> : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={fkspStatusLabelCs(req.status)} color={fkspStatusChipColor(req.status)} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      {req.financist_processed_at ? (
                        <Chip label="Zpracováno" size="small" sx={{ bgcolor: '#fff59d' }} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FkspTablePagination
            count={displayHistory.length}
            page={historyPage}
            onPageChange={setHistoryPage}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{actionType === 'approve' ? 'Doporučit žádost' : 'Nedoporučit žádost'}</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <Typography mb={2}>
            Opravdu chcete {actionType === 'approve' ? 'doporučit' : 'nedoporučit'} žádost uživatele {selectedRequest?.requester_name}?
          </Typography>
          <TextField
            fullWidth label="Poznámka (nepovinné)" multiline rows={3}
            value={note} onChange={e => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Zrušit</Button>
          <Button onClick={handleAction} variant="contained" color={actionType === 'approve' ? 'success' : 'error'}>
            Potvrdit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.show} autoHideDuration={6000} onClose={() => setToast({ ...toast, show: false })}>
        <Alert severity={toast.type} onClose={() => setToast({ ...toast, show: false })}>{toast.msg}</Alert>
      </Snackbar>

      <FkspRequestModal
        open={selectedReq !== null}
        onClose={() => setSelectedReq(null)}
        request={selectedReq}
        canApprove={selectedReq?.zocmos_status === 'pending' || selectedReq?.zocmos_status == null}
        loading={actionLoading}
        onApprove={async (id, note) => {
          setActionLoading(true)
          try {
            await updateFkspRequestStatus(id, 'zocmos', 'approve', note)
            setToast({ show: true, msg: 'Stav byl úspěšně změněn', type: 'success' })
            setSelectedReq(null)
            fetchRequests()
          } catch (err: any) {
            setToast({ show: true, msg: err.message, type: 'error' })
          } finally {
            setActionLoading(false)
          }
        }}
        onReject={async (id, note) => {
          setActionLoading(true)
          try {
            await updateFkspRequestStatus(id, 'zocmos', 'reject', note)
            setToast({ show: true, msg: 'Stav byl úspěšně změněn', type: 'success' })
            setSelectedReq(null)
            fetchRequests()
          } catch (err: any) {
            setToast({ show: true, msg: err.message, type: 'error' })
          } finally {
            setActionLoading(false)
          }
        }}
        onDelete={isItAdmin ? async (id) => {
          const ok = window.confirm('Opravdu chcete tuto žádost smazat? Žádost bude přesunuta do archivu smazaných (viditelného pouze správci IT).')
          if (!ok) return
          setActionLoading(true)
          try {
            await deleteFkspRequest(id)
            setToast({ show: true, msg: 'Žádost byla smazána', type: 'success' })
            setSelectedReq(null)
            fetchRequests()
          } catch (err: any) {
            setToast({ show: true, msg: err.message || 'Chyba při mazání', type: 'error' })
          } finally {
            setActionLoading(false)
          }
        } : undefined}
        onDownloadPdf={async (req) => {
          try {
            await downloadFkspPdf(req.id, req.type)
          } catch (e: any) {
            setToast({ show: true, msg: e?.message || 'Chyba PDF', type: 'error' })
          }
        }}
      />
    </Box>
  )
}
